import {
  DeleteOutlined,
  ExclamationCircleOutlined,
  FileOutlined,
  GlobalOutlined,
  InfoCircleOutlined,
  MailOutlined,
  MobileOutlined,
  SafetyOutlined,
} from '@ant-design/icons';
// import { ReactComponent as ZapierIcon } from 'assets/icons/zapier-type.svg';
import { Button, Input, Tooltip } from '@mm/components';
import { landmineNames } from '@mm/constants/landmine-names';
import {
  AngleDownLightIcon,
  GoogleSheetsIcon,
  KlaviyoIcon,
  WebhookIcon,
  ZapierIcon,
} from '@mm/icons';
import { useGoogleSheetsCRUDActions } from '@mm/state/actions/GoogleSheetsCRUDActions';
import { useSuppressionListsCRUDActions } from '@mm/state/actions/SuppressionListsCRUDActions';
import ContactsController from '@mm/state/controllers/ContactsCotroller';
import SuppressionSettingsController from '@mm/state/controllers/SuppressionSettingsController';
import {
  CaptureActionEnum,
  SuppressionSettingsCSVResponse,
  SuppressionSettingsModel,
} from '@mm/state/model/SuppressionSettingsModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import {
  IGoogleSpreadsheetModal,
  IImportContactListModal,
} from '@mm/state/modules/modalsState/props';
import {
  selectKlaviyoSuppressionList,
  selectWebhookSuppressionList,
  selectZapierSuppressionList,
} from '@mm/state/modules/suppressionListsTableState/selectors';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeFacebookPageIdSelector } from '@mm/state/selectors/viewSelectors';
import { ReactComponent as ManualSuppressionIcon } from 'assets/customize.svg';
import { ReactComponent as CSVIcon } from 'assets/icons/csv-file.svg';
import { ReactComponent as GoogleIcon } from 'assets/icons/google-sheet.svg';
import classNames from 'classnames';
import { colors } from 'packages/utils';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  errorToast,
  successToast,
} from '../../../../../../../../common/helpers/toast';
import { isUrl } from '../../../../../../../components/SlideoutMenu/menus/SlideoutEmail/editor/utils';
import { BaseIntegration } from '../../../BaseIntegration/BaseIntegration';
import { BaseIntegrationHeader } from '../../../BaseIntegration/BaseIntegrationHeader';
import { ReactComponent as TableIcon } from '../../../icons/table.svg';
import { ReactComponent as ZapierSuppressionIcon } from '../../../icons/zapier-suppression.svg';
import { IntegrationSectionProps } from '../../../IntegrationsPanel/IntegrationsPanel';
import { KlaviyoApiKeyConnection } from '../KlaviyoIntegrations/KlaviyoApiKeyConnection';
import { KlaviyoSuppressionSetup } from '../KlaviyoIntegrations/KlaviyoSuppressionSetup';
import { useConnectKlaviyo } from '../KlaviyoIntegrations/use-connect-klaviyo';
import css from './ContactSuppression.module.css';
import { ReactComponent as AddWebhookIcon } from '../../../icons/add-webhook.svg';
import SuppressionWebhookLists from './SuppressionWebhookLists';
import { useViewActions } from '@mm/state/actions/viewActions';
import { autoIncrementedName } from '../../../../../../../../common/helpers/name-autoincrement';
import { GoHighLevelIntegration } from '../GoHighLevelIntegrations/GoHighLevelIntegrations';
import { ShopifyIntegrations } from '../ShopifyIntegrations/ShopifyIntegrations';
import { ZapierApiKey } from '../../../../../../zapier/panels/ZapierPanel/ZapierApiKey';
import { BaseIntegrationEmptyState } from '../../../BaseIntegration/BaseIntegrationEmptyState';
import { StoreState } from '@mm/state/store-interfaces';
import { ZapierSuppressionLists } from './ZapierSuppressionLists/ZapierSuppressionLists';

const exampleData = `larry@customers.ai, +6175551111, customers.ai`;
export const csvExampleBody = encodeURI(
  `data:text/csv;charset=utf-8,${exampleData}`,
);

const csvExampleName = 'contact-suppression-list.csv';

export type ContactSuppressionProps = {
  className?: string;
  captureAction: CaptureActionEnum;
};

export const ContactSuppression: React.FC<
  ContactSuppressionProps & IntegrationSectionProps
> = ({ className, section, captureAction }) => {
  const modalActions = useModalActions();
  const facebookPageId = useSelector(activeFacebookPageIdSelector);
  const apiKeys = useSelector((state: StoreState) => state.apiKeys.entities);
  const zapierKeys = apiKeys.filter(k => k.connectionType === 'Zapier');
  const apiKey = zapierKeys.find(k => k.active);
  const googleSheetsCRUDActions = useGoogleSheetsCRUDActions();
  const isSuppression = captureAction === CaptureActionEnum.do_not_send;

  const klaviyoSuppressions = useSelector(selectKlaviyoSuppressionList);
  const webhookSuppressions = useSelector(selectWebhookSuppressionList).filter(
    w =>
      isSuppression
        ? w.captureAction === captureAction || !w.captureAction
        : w.captureAction === captureAction,
  );
  const zapierSuppressions = useSelector(selectZapierSuppressionList);

  const { showSlideoutMenu } = useViewActions();
  const { create, retrieveAll: retriveAllSuppressionLists } =
    useSuppressionListsCRUDActions();

  const [setting, setSetting] = useState<
    SuppressionSettingsModel | undefined
  >();
  const [settings, setSettings] = useState<SuppressionSettingsModel[]>([]);

  const [busyCustom, setBusyCustom] = useState(false);

  const [oauthUrl, setOauthUrl] = useState('');

  const [uploadedCSVFiles, setUploadedCSVFiles] = useState<
    SuppressionSettingsCSVResponse | undefined
  >();

  const fetchSettings = async () => {
    const res = await SuppressionSettingsController.getSettings(facebookPageId);
    setSettings(res);
    let resObj = res.find(setting => setting.captureAction === captureAction);
    const sendSignalSetting = res.find(s => s.data?.sendSignal);

    if (!resObj?.id) {
      resObj = await SuppressionSettingsController.createSettings(
        facebookPageId,
        { captureAction },
      );

      resObj = await SuppressionSettingsController.updateSettings(
        facebookPageId,
        resObj.id,
        { data: sendSignalSetting?.data },
      );
    }

    setSetting(resObj);
    setCustomListSource(resObj?.list || []);
  };

  const fetchCsvFiles = async () => {
    const files = await SuppressionSettingsController.getUploadedCsvFiles({
      facebookPageId,
      captureAction,
    });
    setUploadedCSVFiles(files);
  };

  const fetchGoogleAccounts = async () => {
    if (facebookPageId) {
      await googleSheetsCRUDActions.getAccounts(facebookPageId);
    }
  };

  const fetchOauthGoogleUrl = async () => {
    if (facebookPageId) {
      const link = await googleSheetsCRUDActions.getOauthUrl(
        window.location.href,
      );
      if (link) {
        setOauthUrl(link);
      }
    }
  };

  const fetchSuppressionLists = async () => {
    if (facebookPageId) {
      await retriveAllSuppressionLists(facebookPageId);
    }
  };

  const { klaviyoConnections, createApiKey, updateApiKey } =
    useConnectKlaviyo();
  const helpLink =
    'https://customers.ai/help/l/en/article/n663a8hz34-klaviyo-integration';
  const hasNativeIntegrationsFeature = useSelector(
    combinedFeatureFlagsSelector,
  ).nativeIntegrations;

  const klaviyoSuppression = klaviyoSuppressions[0];
  const klaviyoConnection = klaviyoConnections[0];

  const { openNewBillingModal2023 } = useModalActions();
  const activeFbPageId = useSelector(activeFacebookPageIdSelector);

  const showUpgradeModal = (onSuccessBilling?: () => void) => {
    openNewBillingModal2023({
      landmineName: landmineNames.KLAVIYO_UPGRADE,
      onSuccessBilling,
    });
  };

  const klaviyoTokenType =
    klaviyoSuppression?.sourceKind === 'klaviyo_oauth' ||
    klaviyoConnection?.klaviyoOauthToken?.id
      ? 'oauth_token'
      : 'api_key';

  const klaviyoTokenId =
    klaviyoSuppression?.sourceId ||
    klaviyoConnection?.klaviyoOauthToken?.id ||
    klaviyoConnection?.klaviyoAccessToken?.id;

  const klaviyoHeader = useMemo(
    () =>
      klaviyoSuppression || klaviyoTokenId ? (
        <div className={css.header}>
          <span>Profiles</span>
          <span className={css.count}>
            {klaviyoSuppression?.profilesCount || 0}
          </span>
          <span className={css.tokenConnected}>Connected</span>
        </div>
      ) : (
        <></>
      ),
    [klaviyoSuppression, klaviyoTokenId],
  );

  const init = async () => {
    try {
      await Promise.all([
        fetchSettings(),
        fetchCsvFiles(),
        fetchGoogleAccounts(),
        fetchOauthGoogleUrl(),
        fetchSuppressionLists(),
      ]);
    } catch (err) {
      errorToast('Error loading contact suppression settings', 'top-right');
    }
  };

  useEffect(() => {
    init();
  }, []);

  const handleManualUploadSubmit: IImportContactListModal['onSubmit'] = async (
    { file, mapping, isFirstRowHeader },
    setLoading,
    setSuccess,
  ) => {
    if (facebookPageId && setting?.id) {
      setLoading(true);
      try {
        const csv = await SuppressionSettingsController.createCSV(
          facebookPageId,
          {
            fileName: file.name,
            sourceSize: file.size,
            mapping,
            headersAtFirstLine: isFirstRowHeader,
            captureAction,
          },
        );

        if (csv?.id && csv?.uploadUrl) {
          const { uploadUrl, id } = csv;
          const uploadResponse = await ContactsController.uploadContactsCsvFile(
            uploadUrl,
            file,
          );

          if (uploadResponse.status !== 200) {
            throw new Error(file.name);
          }

          await SuppressionSettingsController.updateCSV(id, {
            uploaded: true,
          });

          successToast('CSV Uploaded');
          await fetchCsvFiles();
        }
      } catch (err) {
        errorToast(
          `Error uploading csv: ${err?.message ? err.message.toString() : ''}`,
          'top-right',
        );
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCSVDelete = async csv => {
    try {
      await SuppressionSettingsController.deleteCSV(csv.id);
      successToast('CSV removed');
      await fetchCsvFiles();
    } catch (err) {
      errorToast(`Error removing csv`, 'top-right');
    }
  };

  const handleImportContactListSuccess: IImportContactListModal['onSuccess'] =
    ({ audienceId }) => {};

  const handleGoogleSpreadSheetAdd: IGoogleSpreadsheetModal['onSubmit'] =
    async ({ account, sheet, spreadsheet, lookupColumnName }) => {
      const defaultFileName = `${String(spreadsheet.name)} - ${String(
        sheet.title,
      )}`;
      const fileName = lookupColumnName
        ? `${defaultFileName} - ${lookupColumnName}`
        : defaultFileName;
      const payload = {
        fileName,
        sourceSize: 1,
        googleAccountId: account.id,
        googleSpreadsheetId: spreadsheet.id,
        googleSpreadsheetTitle: String(spreadsheet.name),
        googleSheetId: sheet.id || -1,
        googleSheetTitle: String(sheet.title),
        googleLookupColumn: lookupColumnName,
        captureAction,
      };

      try {
        await SuppressionSettingsController.createCSV(facebookPageId, payload);

        successToast(`Connected: "${fileName}"`);
        await fetchCsvFiles();
        modalActions.closeModal();
      } catch (err) {
        errorToast(
          `Error connecting: ${err?.message ? err.message.toString() : ''}`,
          'top-right',
        );
      }
    };

  const handleGoogleSheetsClick = () => {
    modalActions.openGoogleSpreadSheetModal({
      onSubmit: handleGoogleSpreadSheetAdd,
      onReject: () => modalActions.closeModal(),
    });
  };
  const handleSuppressionWebhookClick = async () => {
    const UNTITLED_SUPPRESSION_NAME = `New Webnhook ${
      isSuppression ? 'Suppression' : 'Unsubscribe'
    } List`;

    const incrementedName =
      webhookSuppressions.length === 0
        ? UNTITLED_SUPPRESSION_NAME
        : autoIncrementedName(
            UNTITLED_SUPPRESSION_NAME,
            webhookSuppressions.map(c => c.name),
          );

    const newSuppressionList = await create(activeFbPageId, {
      enabled: false,
      sourceKind: 'suppression_webhook',
      name: incrementedName,
      captureAction,
    });

    showSlideoutMenu('editWebhoookSuppressionListEditor', {
      suppressionListId: newSuppressionList.id,
    });
  };

  const handleCSVUploadClick = () => {
    modalActions.openImportContactListModal({
      title: 'Upload Contact Suppression CSV Manually',
      helpLink: false,
      withMapper: false,
      csvExampleBody,
      csvExampleName,
      onSuccess: handleImportContactListSuccess,
      onSubmit: handleManualUploadSubmit,
    });
  };

  const handleZapierSuppressionClick = async () => {
    const UNTITLED_SUPPRESSION_NAME = 'Zapier List';
    await create(activeFbPageId, {
      enabled: true,
      sourceKind: 'zapier',
      sourceId: apiKey?.id,
      name: autoIncrementedName(
        UNTITLED_SUPPRESSION_NAME,
        zapierSuppressions.map(c => c.name),
      ),
      captureAction,
    });
  };

  const handleCsvDeleteClick = value => {
    modalActions.openNewConfirmModal({
      title: `List of emails and domains`,
      subtitle: `Are you sure you want to remove "${value.name}"?`,
      onResolve: async () => {
        await handleCSVDelete(value);
        modalActions.closeModal();
      },
      onReject: modalActions.closeModal,
    });
  };

  const getClassNameByType = s => {
    switch (s) {
      case 'google_sheet':
        return css.google;
      case 'csv_file':
        return css.csv;
      case 'zapier':
        return 'zapier';
      default:
        return '';
    }
  };

  const getIconByType = s => {
    switch (s) {
      case 'google_sheet':
        return <GoogleIcon />;
      case 'csv_file':
        return <CSVIcon />;
      case 'zapier':
        return <ZapierIcon />;
      default:
        return <></>;
    }
  };

  const CAP = 3;

  const csvs = (uploadedCSVFiles?.csvs || []).map(v => ({
    id: v.id,
    name: v.fileName,
    type: v.kind,
    count: v.recordsCount,
    refreshedAt: v.refreshedAt,
    processed: v.processed,
    captureAction: v.captureAction || CaptureActionEnum.do_not_send,
  }));

  const data = [...csvs];

  const sectionProps = {
    landmineName: section
      ? landmineNames?.[section?.landmineName]
      : 'CONTACT_SUPPRESSION',
    featureFlag: section?.requiredFeature,
  };

  const [viewFull, setViewFull] = useState(false);

  const [customListSource, setCustomListSource] = useState<(string | number)[]>(
    [],
  );

  const customListView = viewFull
    ? customListSource
    : customListSource.slice(0, CAP);

  const onFullListClick = () => {
    setViewFull(true);
  };

  const handleDelete = async id => {
    setBusyCustom(true);

    try {
      if (setting?.id) {
        const list = Array.from(
          new Set(customListSource.filter(vid => vid !== id)),
        );
        await SuppressionSettingsController.updateSettings(
          facebookPageId,
          setting.id,
          {
            ...setting,
            listToDelete: [...(setting.listToDelete || []), id],
          },
        );

        setCustomListSource(list);
        successToast(`Removed: "${id}"`);
      }
    } catch (err) {
      errorToast(`Error removing: "${id}"`, 'top-right');
    } finally {
      setBusyCustom(false);
    }
  };

  const handleCustomDeleteClick = value => {
    modalActions.openNewConfirmModal({
      title: `Manually add Emails/Domains`,
      subtitle: `Are you sure you want to remove "${value}"?`,
      onResolve: async () => {
        await handleDelete(value);
        modalActions.closeModal();
      },
      onReject: modalActions.closeModal,
    });
  };

  const handleAddCustomSuppressionClick = async value => {
    setBusyCustom(true);

    try {
      if (setting?.id) {
        const list = Array.from(new Set([value, ...customListSource]));
        await SuppressionSettingsController.updateSettings(
          facebookPageId,
          setting.id,
          {
            ...setting,
            list,
            captureAction,
          },
        );
        setCustomListSource(list);
        setInput('');
        successToast(`Added: "${value}"`);
      }
    } catch (err) {
      errorToast(`Error adding: "${value}"`, 'top-right');
    } finally {
      setBusyCustom(false);
    }
  };

  const [input, setInput] = useState('');

  const header = ({ onClick, count, withNote = false }) => (
    <div className={css.header}>
      {withNote && (
        <div className={css.info}>
          <InfoCircleOutlined className={css.infoIcon} /> Updated every hour{' '}
        </div>
      )}
      {!!count && (
        <div className={css.listStat}>
          <FileOutlined className={css.fileIcon} />
          <div className={css.column}>
            <span className={css.count}>{count}</span>
            Lists
          </div>
        </div>
      )}
      <Button
        color="secondary-blue"
        css={{ container: 'integrations__header-add-button-secondary' }}
        onClick={e => {
          e?.stopPropagation();
          onClick();
        }}
      >
        Add a List
      </Button>
    </div>
  );

  const lists = data => (
    <div className={css.list}>
      {data.map(item => (
        <div className={css.listItem} key={item.id}>
          <div
            className={classNames(css.listIcon, {
              [getClassNameByType(item.type)]: !!item.type,
            })}
          >
            {getIconByType(item.type)}
          </div>
          <span className={css.listTitle}>{item.name}</span>
          {item.processed ? (
            <span className={css.listCount}>{item.count}</span>
          ) : (
            <Tooltip
              title="There was an issue accessing the file. Please reconnect to try
              again."
            >
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
            </Tooltip>
          )}
          <span className={css.listActions}>
            <div className={css.listAction}>
              <div
                className={css.listActionIcon}
                onClick={() => handleCsvDeleteClick(item)}
              >
                <DeleteOutlined />
              </div>
            </div>
          </span>
        </div>
      ))}
    </div>
  );

  const googleSheetsConnected = data.filter(
    item =>
      item.type === 'google_sheet' && item.captureAction === captureAction,
  );

  const csvConnected = data.filter(
    item => item.type === 'csv_file' && item.captureAction === captureAction,
  );

  return (
    <div className={classNames(className, css.container)}>
      <div className={css.note}>
        <SafetyOutlined className={css.noteIcon} />
        All {isSuppression ? 'suppression' : 'unsubscribe'} lists uploaded will
        remain confidential.
      </div>
      {isSuppression && (
        <BaseIntegration
          titleComponent={
            <BaseIntegrationHeader
              title="Klaviyo"
              icon={<KlaviyoIcon />}
              headerRightComponent={klaviyoHeader}
              css={{ icon: 'integrations__klaviyo-header-icon' }}
              {...sectionProps}
            />
          }
          showEmptyState={false}
        >
          {klaviyoTokenId === undefined ? (
            <KlaviyoApiKeyConnection
              hasNativeIntegrationsFeature={hasNativeIntegrationsFeature}
              helpLink={helpLink}
              controlsDisabled={false}
              token={undefined}
              createApiKey={createApiKey}
              updateApiKey={updateApiKey}
              showUpgradeModal={showUpgradeModal}
            />
          ) : (
            <KlaviyoSuppressionSetup
              helpLink={helpLink}
              controlsDisabled={false}
              tokenType={klaviyoTokenType}
              tokenId={klaviyoTokenId}
              withNavLink={false}
              activeFbPageId={activeFbPageId}
            />
          )}
        </BaseIntegration>
      )}

      {isSuppression && (
        <>
          <ShopifyIntegrations version="suppression" />
          <GoHighLevelIntegration version="suppression" />
        </>
      )}

      <BaseIntegration
        titleComponent={
          <BaseIntegrationHeader
            title="Google Sheets"
            icon={<GoogleSheetsIcon />}
            haveErrors={googleSheetsConnected.some(
              sheet => sheet?.processed === false,
            )}
            headerRightComponent={header({
              onClick: handleGoogleSheetsClick,
              count: googleSheetsConnected?.length,
              withNote: true,
            })}
            css={{ icon: 'integrations__header-icon-googlesheets' }}
            {...sectionProps}
          />
        }
        showEmptyState={!googleSheetsConnected.length}
        emptyStateConfig={{
          button: 'Add a Google Sheets List',
          onClick: handleGoogleSheetsClick,
          icon: <TableIcon />,
          ...sectionProps,
        }}
      >
        {lists(googleSheetsConnected)}
      </BaseIntegration>

      <BaseIntegration
        titleComponent={
          <BaseIntegrationHeader
            title="CSV"
            subtitle="Upload CSV Manually"
            icon={<CSVIcon />}
            haveErrors={csvConnected.some(csv => csv?.processed === false)}
            headerRightComponent={header({
              count: csvConnected?.length,
              onClick: handleCSVUploadClick,
            })}
            css={{ icon: 'integrations__header-icon-csv' }}
            {...sectionProps}
          />
        }
        showEmptyState={!csvConnected.length}
        emptyStateConfig={{
          button: 'Add a CSV List',
          onClick: handleCSVUploadClick,
          icon: <TableIcon />,
          ...sectionProps,
        }}
      >
        {lists(csvConnected)}
      </BaseIntegration>

      <BaseIntegration
        titleComponent={
          <BaseIntegrationHeader
            title="JSON Webhooks "
            subtitle="Automatically fetch your data from JSON Webhook"
            icon={<WebhookIcon color="white" />}
            headerRightComponent={header({
              onClick: handleSuppressionWebhookClick,
              count: webhookSuppressions?.length,
              withNote: true,
            })}
            upgradeButtonName="Add a List"
            css={{ icon: 'integrations__webhook-header-icon' }}
            haveErrors={webhookSuppressions.some(
              suppressionList =>
                !!suppressionList.suppressionWebhook?.errorInfo,
            )}
            {...sectionProps}
          />
        }
        integrationType="default"
        emptyStateConfig={{
          button: 'Add a JSON Webhooks Connection',
          onClick: handleSuppressionWebhookClick,
          icon: <AddWebhookIcon />,
          ...sectionProps,
        }}
        showEmptyState={!webhookSuppressions.length}
      >
        <SuppressionWebhookLists captureAction={captureAction} />
      </BaseIntegration>

      <BaseIntegration
        titleComponent={
          <BaseIntegrationHeader
            title="Manually add Emails/Domains"
            subtitle="Choose emails, domains, and phone numbers that your automations will not engage with.
            We automatically suppress bounces, unsubscribes and other emails we believe to be problematic."
            icon={<ManualSuppressionIcon />}
            css={{ icon: 'integrations__header-icon-manual-suppression' }}
            {...sectionProps}
          />
        }
      >
        <section>
          <div className={css.inputField}>
            <Input
              type="text"
              placeholder={`Enter domains/emails/phone numbers to ${
                isSuppression ? 'suppress' : 'unsubscribe'
              }`}
              value={input}
              onChange={e => setInput(e.target.value.trim())}
              css={{
                container: css.input,
                input: css.inputArea,
              }}
            />
            <Button
              onClick={() => handleAddCustomSuppressionClick(input)}
              disabled={!input || busyCustom}
              css={{ container: css.primaryButton }}
            >
              Add
            </Button>
          </div>

          <div className={css.inputList}>
            {customListView?.map(item => {
              const icon =
                typeof item === 'string' && item.includes('@') ? (
                  <MailOutlined />
                ) : typeof item === 'string' && item.startsWith('+') ? (
                  <MobileOutlined />
                ) : typeof item === 'string' && isUrl(item) ? (
                  <GlobalOutlined />
                ) : null;
              return (
                <div className={css.listItem} key={item}>
                  <span className={css.inputListTitle}>
                    {icon}
                    {item}
                  </span>
                  <span className={css.inputListActions}>
                    <div className={css.inputListAction}>
                      <div
                        className={css.listAction}
                        onClick={
                          busyCustom
                            ? undefined
                            : () => handleCustomDeleteClick(item)
                        }
                      >
                        <DeleteOutlined />
                      </div>
                    </div>
                  </span>
                </div>
              );
            })}

            {customListSource.length > CAP && !viewFull && (
              <div className={css.inputListItem}>
                <span
                  className={classNames(css.inputListTitle, css.clickable)}
                  onClick={onFullListClick}
                >
                  View full list <AngleDownLightIcon color={colors.gray9} />
                </span>
              </div>
            )}
          </div>
        </section>
      </BaseIntegration>

      {isSuppression && (
        <BaseIntegration
          titleComponent={
            <BaseIntegrationHeader
              title="Zapier"
              icon={<ZapierIcon />}
              headerRightComponent={header({
                count: zapierSuppressions?.length || 0,
                onClick: handleZapierSuppressionClick,
              })}
              css={{ icon: 'integrations__zapier-header-icon' }}
              {...sectionProps}
            />
          }
        >
          <div className={css.suppressionContainer}>
            <ZapierApiKey isSuppression />
            {zapierSuppressions.length ? (
              <ZapierSuppressionLists />
            ) : (
              <BaseIntegrationEmptyState
                button="Add a Zapier List"
                onClick={handleZapierSuppressionClick}
                icon={<ZapierSuppressionIcon />}
                landmineName={landmineNames.INTEGRATIONS_CONTACT_SUPPRESSION}
              />
            )}
          </div>
        </BaseIntegration>
      )}
    </div>
  );
};
